/**
 * V66 — International hreflang / canonical strategy (single source of truth).
 *
 * Google's hreflang rules require the *same* alternate cluster to be emitted
 * from every URL in a language / region group, otherwise Google treats the
 * group as broken and quietly serves the wrong regional URL to users.
 *
 * To make this bullet-proof we keep ONE map here, and every hub page (Home,
 * /online-classes, /online-tuition-worldwide, every /online-tuition-{country})
 * pulls the same cluster via `getInternationalHreflangs()`.
 *
 * Canonical strategy
 * ─────────────────────────────────────────────────────────────────────────
 *   • en-IN  → /online-classes                (India — pan-India hub)
 *   • en-XX  → /online-tuition-{country-slug}  (per-country hub, XX = ISO)
 *   • x-default → /online-classes             (Google routes unmatched
 *                                              locales to the India hub;
 *                                              in reality it's the widest
 *                                              English page we serve.)
 *
 * NOTE: each country page ALSO emits its own <link rel="canonical"> pointing
 * to itself (via the SEO component's `path` prop). hreflang is a set of
 * alternates — it does not override the canonical, it complements it.
 */

const SITE_URL = "https://anandtutorials.com";

// Country slug → { hreflang, path }.
// Ordered by traffic priority (large NRI diaspora first, then GCC, then Europe).
const INTERNATIONAL_ALTERNATES = [
    // North America
    { slug: "usa", hreflang: "en-US", path: "/online-tuition-usa" },
    { slug: "canada", hreflang: "en-CA", path: "/online-tuition-canada" },

    // UK + Ireland
    { slug: "uk", hreflang: "en-GB", path: "/online-tuition-uk" },
    { slug: "ireland", hreflang: "en-IE", path: "/online-tuition-ireland" },

    // Asia-Pacific
    { slug: "australia", hreflang: "en-AU", path: "/online-tuition-australia" },
    { slug: "new-zealand", hreflang: "en-NZ", path: "/online-tuition-new-zealand" },
    { slug: "singapore", hreflang: "en-SG", path: "/online-tuition-singapore" },
    { slug: "malaysia", hreflang: "en-MY", path: "/online-tuition-malaysia" },
    { slug: "japan", hreflang: "en-JP", path: "/online-tuition-japan" },
    { slug: "south-korea", hreflang: "en-KR", path: "/online-tuition-south-korea" },

    // Middle East / GCC
    { slug: "uae", hreflang: "en-AE", path: "/online-tuition-uae" },
    { slug: "saudi-arabia", hreflang: "en-SA", path: "/online-tuition-saudi-arabia" },
    { slug: "qatar", hreflang: "en-QA", path: "/online-tuition-qatar" },
    { slug: "kuwait", hreflang: "en-KW", path: "/online-tuition-kuwait" },
    { slug: "oman", hreflang: "en-OM", path: "/online-tuition-oman" },
    { slug: "bahrain", hreflang: "en-BH", path: "/online-tuition-bahrain" },

    // Europe
    { slug: "germany", hreflang: "en-DE", path: "/online-tuition-germany" },
    { slug: "france", hreflang: "en-FR", path: "/online-tuition-france" },
    { slug: "netherlands", hreflang: "en-NL", path: "/online-tuition-netherlands" },
    { slug: "switzerland", hreflang: "en-CH", path: "/online-tuition-switzerland" },
    { slug: "belgium", hreflang: "en-BE", path: "/online-tuition-belgium" },
    { slug: "italy", hreflang: "en-IT", path: "/online-tuition-italy" },
    { slug: "spain", hreflang: "en-ES", path: "/online-tuition-spain" },
    { slug: "norway", hreflang: "en-NO", path: "/online-tuition-norway" },
    { slug: "sweden", hreflang: "en-SE", path: "/online-tuition-sweden" },
    { slug: "finland", hreflang: "en-FI", path: "/online-tuition-finland" },
    { slug: "denmark", hreflang: "en-DK", path: "/online-tuition-denmark" },
];

// The India-canonical URL — used as en-IN and x-default.
const INDIA_CANONICAL_PATH = "/online-classes";

/**
 * Return the full hreflang cluster for any hub page in the international
 * online-tuition family. The SEO component's `hreflangs` prop expects
 * [{ lang, path }] shape.
 *
 * Every hub URL emits the SAME cluster — that's what Google requires for the
 * cluster to be considered "valid" and for regional routing to work.
 *
 * `hreflangs` from the SEO component automatically appends an `x-default`
 * → canonicalUrl. But because our x-default should point to /online-classes
 * (not to the current URL), we prepend an explicit x-default row here so the
 * SEO component's fallback appends AFTER our explicit one — Google honours
 * the first hreflang tag for a given locale, so this order matters.
 */
export function getInternationalHreflangs() {
    return [
        // India — pan-India online-classes hub is the en-IN canonical.
        { lang: "en-IN", path: INDIA_CANONICAL_PATH },
        // All country pages.
        ...INTERNATIONAL_ALTERNATES.map((a) => ({ lang: a.hreflang, path: a.path })),
        // x-default — Google falls back here when no locale matches.
        { lang: "x-default", path: INDIA_CANONICAL_PATH },
    ];
}

export { INTERNATIONAL_ALTERNATES, SITE_URL, INDIA_CANONICAL_PATH };
